import { useState } from "react";
import modalclose from "../images/modalclose.svg";
import eye from "../images/eye.svg";
import { useDispatch } from "react-redux";
import { setLoginModal } from "../store/auth-slice";
import { handleApiError } from "../errors";
import {
  useCompletePasswordResetMutation,
  useInitiatePasswordResetMutation,
  useVerifyOtpMutation,
} from "../store/apis";
import { toastService } from "../screens/notificationPage/ToastContainer";
import { Eye } from "lucide-react";

const PasswordReset = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1); // 1: Request OTP, 2: Enter OTP, 3: New Password
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [initiatePasswordReset, { isLoading: isInitiatePasswordResetLoading }] =
    useInitiatePasswordResetMutation();
  const [verifyOtp, { isLoading: isVerifyOtpLoading }] = useVerifyOtpMutation();
  const [completePasswordReset, { isLoading: isCompletePasswordResetLoading }] =
    useCompletePasswordResetMutation();

  const [formData, setFormData] = useState({
    phone: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [resetTokenId, setResetTokenId] = useState(null);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    setError("");
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();

    if (!formData.phone) {
      setError("Phone number is required");
      return;
    }

    try {
      const response = await initiatePasswordReset({
        username: `251${formData.phone}`,
      }).unwrap();
      toastService.success(response.message || "OTP sent successfully");
      setStep(2);
    } catch (error) {
      setError(error.data?.message || "Failed to send code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!formData.otp) {
      setError("Code is required");
      return;
    }

    try {
      const response = await verifyOtp({
        username: `251${formData.phone}`,
        otp: formData.otp,
      }).unwrap();
      toastService.success(response.message || "verified successfully");
      setResetTokenId(response.resetTokenId);
      setStep(3);
    } catch (error) {
      setError(error.data?.message || "Invalid code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!formData.newPassword) {
      setError("New password is required");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      await completePasswordReset({
        username: `251${formData.phone}`,
        newPassword: formData.newPassword,
        resetTokenId: resetTokenId,
      }).unwrap();
      onClose();
      dispatch(setLoginModal(true));
    } catch (error) {
      setError(error.data?.message || "Password reset failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50">
      <div className="bg-[#061015] w-full max-w-md rounded-lg overflow-hidden border border-gray-800">
        <div className="p-6">
          <div className="flex justify-between items-center pb-[10px] mb-5 border-b-[1px] border-[#33494E]">
            <h4 className="text-white text-2xl font-medium">
              {step === 1 && "RESTORE PASSWORD"}
              {step === 2 && "VERIFY CODE"}
              {step === 3 && "SET NEW PASSWORD"}
            </h4>
            <button
              onClick={onClose}
              className="w-[38px] h-[38px] min-w-[38px] flex justify-center items-center border-[1px] rounded-md border-[#33494E] bg-[rgba(0, 0, 0, 0.3)] hover:bg-[rgba(0, 0, 0, 0.5)] transition-all duration-[0.4s]"
            >
              <img src={modalclose} alt="close" />
            </button>
          </div>
          {/* <h2 className="text-white text-2xl font-medium text-center mb-6 border-b border-gray-800 pb-4">
            {step === 1 && "RESTORE PASSWORD"}
            {step === 2 && "VERIFY CODE"}
            {step === 3 && "SET NEW PASSWORD"}
          </h2> */}

          {step === 1 && (
            <>
              <div className="text-gray-300 text-sm mb-6">
                Follow the following to reset password
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Phone number</label>
                <div className="flex">
                  <div className="bg-[#0a1a20] text-green-500 px-3 py-2 border border-gray-700 rounded-l flex items-center justify-center">
                    +251
                  </div>
                  <input
                    type="text"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    className="w-full p-2 bg-[#0a1a20] border border-gray-700 text-white rounded-r"
                  />
                  <button
                    onClick={handleRequestOTP}
                    disabled={isLoading}
                    className="ml-2 bg-[#142a35] hover:bg-[#1c3a4a] text-gray-300 px-4 py-2 rounded transition"
                  >
                    {isLoading ? "..." : "Get Code"}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Code</label>
                <input
                  type="text"
                  id="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  placeholder="Enter Code"
                  className="w-full p-2 bg-[#0a1a20] border border-gray-700 text-white rounded"
                  disabled={!formData.phone}
                />
              </div>

              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

              <div className="mt-6">
                <button
                  onClick={formData.otp ? handleVerifyOTP : handleRequestOTP}
                  disabled={isLoading || (!formData.phone && !formData.otp)}
                  className="w-full bg-[#142a35] hover:bg-[#1c3a4a] text-gray-300 py-3 rounded-md transition"
                >
                  {isInitiatePasswordResetLoading
                    ? "Processing..."
                    : formData.otp
                    ? "Restore Password"
                    : "Get Code"}
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-gray-300 text-sm mb-6">
                A verification code has been sent to your phone number +251
                {formData.phone}
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Code</label>
                <input
                  type="text"
                  id="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  placeholder="Enter Code"
                  className="w-full p-2 bg-[#0a1a20] border border-gray-700 text-white rounded"
                />
              </div>

              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

              <div className="mt-6">
                <button
                  onClick={handleVerifyOTP}
                  disabled={isVerifyOtpLoading || !formData.otp}
                  className="w-full bg-[#142a35] hover:bg-[#1c3a4a] text-gray-300 py-3 rounded-md transition"
                >
                  {isVerifyOtpLoading ? "Verifying..." : "Verify Code"}
                </button>
              </div>

              <div className="mt-4 flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="text-gray-400 hover:text-gray-300 text-sm"
                >
                  Change Phone Number
                </button>
                <button
                  onClick={handleRequestOTP}
                  className="text-green-500 hover:text-green-400 text-sm"
                >
                  Resend Code
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    className="w-full p-2 bg-[#0a1a20] border border-gray-700 text-white rounded pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    <Eye size={20} />
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    className="w-full p-2 bg-[#0a1a20] border border-gray-700 text-white rounded pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                  >
                    <Eye size={20} />
                  </button>
                </div>
              </div>

              {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

              <div className="mt-6">
                <button
                  onClick={handleResetPassword}
                  disabled={
                    isCompletePasswordResetLoading ||
                    !formData.newPassword ||
                    !formData.confirmPassword
                  }
                  className="w-full bg-[#142a35] hover:bg-[#1c3a4a] text-gray-300 py-3 rounded-md transition"
                >
                  {isCompletePasswordResetLoading
                    ? "Updating..."
                    : "Update Password"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
